import React, { useState, useEffect } from 'react';
import { analytics } from '../utils/analytics';

const ChatWidget = ({ providerId, providerName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isEnabled] = useState(process.env.REACT_APP_ENABLE_CHAT === 'true');

  useEffect(() => {
    if (isEnabled && isOpen) {
      analytics.trackFeatureUsage('chat_opened', { providerId, providerName });
    }
  }, [isOpen, providerId, providerName, isEnabled]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const message = {
      id: Date.now(),
      text: newMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, message]);
    setNewMessage('');

    // Track message sent
    analytics.trackFeatureUsage('chat_message_sent', {
      providerId,
      providerName,
      messageLength: newMessage.length
    });

    // Simulate provider response
    setTimeout(() => {
      const response = {
        id: Date.now(),
        text: "Thanks for your message! We will get back to you soon.",
        sender: 'provider',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, response]);
    }, 1000);
  };

  // Early return if chat is disabled
  const chatEnabled = process.env.REACT_APP_ENABLE_CHAT === 'true';
  if (!chatEnabled) return null;

  return (
    <div className="chat-widget">
      <button 
        className="chat-toggle"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? '✕' : '💬'}
      </button>

      {isOpen && (
        <div className="chat-container">
          <div className="chat-header">
            <h3>Chat with {providerName}</h3>
          </div>

          <div className="chat-messages">
            {messages.length === 0 ? (
              <div className="chat-empty">
                Start a conversation with this provider
              </div>
            ) : (
              messages.map(message => (
                <div 
                  key={message.id}
                  className={`chat-message ${message.sender}`}
                >
                  <div className="message-text">{message.text}</div>
                  <div className="message-time">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              ))
            )}
          </div>

          <form onSubmit={handleSubmit} className="chat-input">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
            />
            <button type="submit">Send</button>
          </form>
        </div>
      )}

      <style>
        {`
          .chat-widget {
            position: fixed;
            bottom: 2rem;
            right: 2rem;
            pointer-events: all;
            z-index: var(--z-index-fixed);
          }

          .chat-toggle {
            background: linear-gradient(145deg, #ff6b35, #e65d2e);
            color: white;
            border: none;
            width: 4rem;
            height: 4rem;
            font-size: 1.75rem;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 8px 24px rgba(255, 107, 53, 0.3);
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid rgba(255,255,255,0.1);
          }

          .chat-toggle:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 32px rgba(255, 107, 53, 0.4);
          }

          .chat-container {
            position: absolute;
            bottom: calc(100% + 1rem);
            right: 0;
            width: 350px;
            height: 500px;
            background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
            border-radius: 12px;
            box-shadow: 0 12px 32px rgba(0,0,0,0.3);
            display: flex;
            flex-direction: column;
            border: 2px solid rgba(255,255,255,0.1);
          }

          .chat-header {
            padding: 1.25rem;
            background: linear-gradient(145deg, #ff6b35, #e65d2e);
            color: #ffffff;
            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
            border-bottom: 2px solid rgba(255,255,255,0.1);
          }

          .chat-header h3 {
            margin: 0;
            font-size: 1rem;
          }

          .chat-messages {
            flex: 1;
            padding: 1.5rem;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            background: linear-gradient(145deg, #222, #1a1a1a);
            gap: 1rem;
          }

          .chat-empty {
            text-align: center;
            color: rgba(255,255,255,0.6);
            margin-top: 2rem;
            font-size: 1.1rem;
            font-weight: 300;
          }

          .chat-message {
            max-width: 80%;
            padding: 0.75rem;
            border-radius: var(--border-radius-sm);
            word-break: break-word;
          }

          .chat-message.user {
            background: linear-gradient(145deg, #ff6b35, #e65d2e);
            color: #ffffff;
            align-self: flex-end;
            margin-left: 20%;
            border-radius: 12px 12px 2px 12px;
            box-shadow: 0 4px 12px rgba(255, 107, 53, 0.2);
          }

          .chat-message.provider {
            background: linear-gradient(145deg, #383838, #2a2a2a);
            color: #fff;
            align-self: flex-start;
            margin-right: 20%;
            border-radius: 12px 12px 12px 2px;
            border: 1px solid rgba(255,255,255,0.1);
          }

          .message-time {
            font-size: 0.8rem;
            opacity: 0.7;
            margin-top: 0.5rem;
            font-weight: 300;
          }

          .chat-input {
            padding: 1.25rem;
            background: linear-gradient(145deg, #2a2a2a, #222);
            display: flex;
            gap: 0.75rem;
            border-top: 2px solid rgba(255,255,255,0.1);
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
          }

          .chat-input input {
            flex: 1;
            padding: 0.75rem 1rem;
            background: linear-gradient(145deg, #222, #1a1a1a);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 6px;
            color: #fff;
            font-size: 1rem;
            transition: all 0.3s ease;
          }

          .chat-input input:focus {
            outline: none;
            border-color: rgba(255, 107, 53, 0.4);
            box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.1);
          }

          .chat-input input::placeholder {
            color: rgba(255,255,255,0.5);
          }

          .chat-input button {
            background: linear-gradient(145deg, #ff6b35, #e65d2e);
            color: white;
            border: none;
            min-width: 80px;
            padding: 0.75rem 1rem;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            transition: all 0.3s ease;
            box-shadow: 0 4px 12px rgba(255, 107, 53, 0.2);
          }

          .chat-input button:hover {
            background: var(--secondary-color);
          }

          @media (max-width: 768px) {
            .chat-widget,
            .chat-container {
            position: absolute;
            bottom: calc(100% + 1rem);
            right: 0;
            width: 350px;
            height: 500px;
            background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
            border-radius: 12px;
            box-shadow: 0 12px 32px rgba(0,0,0,0.3);
            display: flex;
            flex-direction: column;
            border: 2px solid rgba(255,255,255,0.1);
          }

            .chat-toggle {
            background: linear-gradient(145deg, #ff6b35, #e65d2e);
            color: white;
            border: none;
            width: 4rem;
            height: 4rem;
            font-size: 1.75rem;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 8px 24px rgba(255, 107, 53, 0.3);
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid rgba(255,255,255,0.1);
          }
          }
        `}
      </style>
    </div>
  );
};

export default ChatWidget;